/**
 * Global Keyboard Shortcut Dispatcher
 * Master Reference: docs/KEYBOARD_AND_INPUT_MASTER.md
 */
import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useShortcutsStore } from "@/lib/shortcuts-store";
import {
  comboFromKeyboardEvent,
  isProtectedBrowserCombo,
  normalizeKeyCombo,
} from "@/lib/keyboard/normalize-combo";
import { dispatchKeyboardAction, KEYBOARD_EVENTS } from "@/lib/keyboard/keyboard-events";
import { useDeviceClass } from "@/hooks/use-device-class";

function shouldEnableGlobalShortcuts(device: ReturnType<typeof useDeviceClass>): boolean {
  // Phone touch UI only — tablets/desktops keep power-user shortcuts (incl. external keyboards).
  return device !== "mobile";
}

export function useGlobalShortcuts() {
  const navigate = useNavigate();
  const shortcuts = useShortcutsStore((s) => s.shortcuts);
  const device = useDeviceClass();

  useEffect(() => {
    if (!shouldEnableGlobalShortcuts(device)) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (isProtectedBrowserCombo(e)) return;

      const target = e.target as HTMLElement | null;
      const isInput =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable ||
          target.tagName === "SELECT");

      const pressedCombo = comboFromKeyboardEvent(e);
      if (!pressedCombo) return;

      for (const s of shortcuts) {
        const targetCombo = normalizeKeyCombo(s.customKeys || s.defaultKeys);
        if (targetCombo !== pressedCombo) continue;

        // In plain inputs: allow only modifier-driven shortcuts.
        if (isInput && !e.altKey && !e.ctrlKey && !e.metaKey) continue;

        const action = s.action ?? (s.routeTarget ? "navigate" : undefined);
        if (!action) continue;

        e.preventDefault();
        e.stopPropagation();

        switch (action) {
          case "navigate":
            if (s.routeTarget) void navigate({ to: s.routeTarget as "/" });
            return;
          case "command_palette":
            dispatchKeyboardAction(KEYBOARD_EVENTS.COMMAND_PALETTE);
            return;
          case "cheat_sheet":
            dispatchKeyboardAction(KEYBOARD_EVENTS.CHEAT_SHEET);
            return;
          case "assistant":
            void navigate({ to: "/ai-center" });
            return;
          case "history_back":
            window.history.back();
            return;
          case "module_launcher":
            dispatchKeyboardAction(KEYBOARD_EVENTS.MODULE_LAUNCHER);
            return;
          default:
            return;
        }
      }
    }

    // Capture phase so Alt+* shortcuts win over browser menu focus on Windows.
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [shortcuts, navigate, device]);
}
